"use client";

/**
 * CompareModal: side-by-side comparison table for shortlisted providers.
 *
 * A11y: focus trap, ESC to close, backdrop click to close, role="dialog",
 * aria-modal="true", auto-focus on first focusable element.
 * Full-width on mobile, max-width constrained on desktop.
 */

import { useEffect, useRef } from "react";
import { X, MapPin, Star, CheckCircle } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "@/lib/utils";
import { ProviderLogoPlaceholder } from "@/components/site/ProviderLogoPlaceholder";
import { getVerifiedProviderAddress } from "@/lib/data/provider-trust-signals";
import type { ProvidersApiResponse } from "@/app/api/providers/route";
import { buildProviderProfilePath } from "@/lib/provider-url";

// ── Focus trap ─────────────────────────────────────────────────────────────

const FOCUSABLE_SELECTORS = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function trapFocus(container: HTMLElement, e: KeyboardEvent) {
  const focusable = Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS),
  ).filter((el) => el.offsetParent !== null);
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];

  if (e.key === "Tab") {
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }
}

// ── Types ──────────────────────────────────────────────────────────────────

type ProviderCard = ProvidersApiResponse["providers"][number];

export type CompareModalProps = {
  open: boolean;
  providers: ProviderCard[];
  onClose: () => void;
  onRemove: (slug: string) => void;
  onClearAll: () => void;
};

// ── Provider column ────────────────────────────────────────────────────────

function ProviderColumn({
  provider,
  onRemove,
}: {
  provider: ProviderCard;
  onRemove: (slug: string) => void;
}) {
  const verifiedAddress = getVerifiedProviderAddress(provider);
  const addressDisplay = verifiedAddress
    ? verifiedAddress
    : [provider.city, provider.state].filter(Boolean).join(", ") || "Location not listed";

  const hasRating = provider.googleRating > 0;
  const hasReviews = provider.googleReviewCount > 0;

  return (
    <div className="flex min-w-[160px] flex-1 flex-col gap-4">
      {/* Remove button + logo */}
      <div className="flex flex-col items-center gap-3 text-center">
        <div className="flex w-full justify-end">
          <button
            type="button"
            onClick={() => onRemove(provider.slug)}
            aria-label={`Remove ${provider.name} from comparison`}
            className="flex h-7 w-7 items-center justify-center rounded-full text-[var(--color-muted)] transition-colors hover:bg-slate-100 hover:text-[var(--color-primary)]"
          >
            <X size={14} aria-hidden="true" />
          </button>
        </div>
        <ProviderLogoPlaceholder
          providerName={provider.name}
          logoUrl={provider.logoUrl}
          sizeClassName="h-14 w-14"
          textSizeClassName="text-lg"
        />
        <h3 className="text-sm font-semibold leading-snug text-[var(--color-primary)]">
          {provider.name}
        </h3>
      </div>

      {/* Address */}
      <div className="rounded-xl border border-[var(--color-border)] bg-slate-50 px-3 py-3">
        <p className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted)]">
          Address
        </p>
        <div className="flex items-start gap-1.5 text-xs text-[var(--color-primary)]">
          <MapPin
            size={11}
            className={cn(
              "mt-0.5 shrink-0",
              verifiedAddress ? "text-[var(--color-accent)]" : "text-[var(--color-muted)]",
            )}
            aria-hidden="true"
          />
          <span className={cn(!verifiedAddress && "text-[var(--color-muted)]")}>
            {addressDisplay}
          </span>
        </div>
      </div>

      {/* Trust signal */}
      <div className="rounded-xl border border-[var(--color-border)] bg-slate-50 px-3 py-3">
        <p className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted)]">
          Rating
        </p>
        {hasRating || hasReviews ? (
          <div className="flex items-center gap-1 text-xs">
            <Star
              size={11}
              className="shrink-0 fill-current text-amber-400"
              aria-hidden="true"
            />
            <span className="font-medium text-[var(--color-primary)]">
              {hasRating ? provider.googleRating.toFixed(1) : "--"}
            </span>
            {hasReviews && (
              <span className="text-[var(--color-muted)]">
                ({provider.googleReviewCount})
              </span>
            )}
          </div>
        ) : (
          <p className="text-xs text-[var(--color-muted)]">No rating yet</p>
        )}
      </div>

      {/* Claim status */}
      <div className="rounded-xl border border-[var(--color-border)] bg-slate-50 px-3 py-3">
        <p className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted)]">
          Claim status
        </p>
        {provider.claimed ? (
          <div className="flex items-center gap-1 text-xs font-medium text-emerald-700">
            <CheckCircle size={11} aria-hidden="true" />
            Claimed
          </div>
        ) : (
          <p className="text-xs text-[var(--color-muted)]">Unclaimed</p>
        )}
      </div>

      {/* Specialty */}
      <div className="rounded-xl border border-[var(--color-border)] bg-slate-50 px-3 py-3">
        <p className="mb-1 text-xs font-semibold uppercase tracking-[0.12em] text-[var(--color-muted)]">
          Specialty
        </p>
        <p className="text-xs text-[var(--color-primary)]">
          {provider.specialty ?? provider.verticalSlug ?? "--"}
        </p>
      </div>

      {/* Profile link */}
      <a
        href={buildProviderProfilePath(provider)}
        className="mt-auto block rounded-lg border border-[var(--color-accent)] px-3 py-2 text-center text-xs font-semibold text-[var(--color-accent)] transition-colors hover:bg-emerald-50"
      >
        View profile
      </a>
    </div>
  );
}

// ── Modal ──────────────────────────────────────────────────────────────────

export function CompareModal({
  open,
  providers,
  onClose,
  onRemove,
  onClearAll,
}: CompareModalProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // ESC + focus trap
  useEffect(() => {
    if (!open) return;

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (dialogRef.current) trapFocus(dialogRef.current, e);
    }

    document.addEventListener("keydown", onKeyDown);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Auto-focus close button
    setTimeout(() => closeButtonRef.current?.focus(), 50);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previous;
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          {/* Backdrop */}
          <motion.div
            key="compare-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/50"
            aria-hidden="true"
            onClick={onClose}
          />

          {/* Dialog panel */}
          <motion.div
            key="compare-dialog"
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label="Compare providers"
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            className="fixed inset-x-4 bottom-4 top-4 z-50 mx-auto flex max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl sm:inset-x-6 md:inset-x-auto md:left-1/2 md:top-1/2 md:h-auto md:max-h-[85vh] md:-translate-x-1/2 md:-translate-y-1/2"
          >
            {/* Header */}
            <div className="flex shrink-0 items-center justify-between border-b border-[var(--color-border)] px-6 py-4">
              <h2 className="text-lg font-semibold text-[var(--color-primary)]">
                Compare providers
                {providers.length > 0 && (
                  <span className="ml-2 text-sm font-normal text-[var(--color-muted)]">
                    ({providers.length} selected)
                  </span>
                )}
              </h2>
              <div className="flex items-center gap-3">
                {providers.length > 0 && (
                  <button
                    type="button"
                    onClick={onClearAll}
                    className="text-sm font-medium text-[var(--color-muted)] hover:text-red-600 transition-colors"
                  >
                    Remove all
                  </button>
                )}
                <button
                  ref={closeButtonRef}
                  type="button"
                  onClick={onClose}
                  aria-label="Close comparison"
                  className="flex h-10 w-10 items-center justify-center rounded-full text-[var(--color-muted)] transition-colors hover:bg-slate-100 hover:text-[var(--color-primary)]"
                >
                  <X size={18} aria-hidden="true" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {providers.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <p className="text-lg font-semibold text-[var(--color-primary)]">
                    No providers selected
                  </p>
                  <p className="mt-2 text-sm text-[var(--color-muted)]">
                    Use the &quot;+ Compare&quot; button on each provider card to add up to 4 providers.
                  </p>
                </div>
              ) : (
                <div className="flex gap-4 overflow-x-auto pb-2">
                  {providers.map((provider) => (
                    <ProviderColumn
                      key={provider.slug}
                      provider={provider}
                      onRemove={onRemove}
                    />
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
